import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import type { Carpeta, RolResumen } from "./tipos"

/**
 * Quién abre esta carpeta.
 *
 * ⚠ **Dejarlo vacío NO cierra la carpeta: la devuelve a lo que diga su rama.** Es la
 * confusión que este panel existe para evitar. Sin decirlo, quien vacía la lista cree
 * haber cerrado la carpeta y en realidad la ha abierto a todo el que abra la de
 * arriba. Por eso el estado heredado se pinta con los roles que se heredan, y no como
 * una lista en blanco.
 *
 * ⚠ **Los permisos propios REEMPLAZAN a los de arriba, no se suman.** Es lo que
 * permite tener «Legal» abierta y «Legal/Confidencial» cerrada dentro. El aviso lo
 * dice en la pantalla porque el comportamiento contrario —heredar y añadir— es el que
 * la gente da por supuesto.
 */

export function AccesosDeCarpeta({
  carpeta,
  onCerrar,
}: {
  carpeta: Carpeta
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [elegidos, setElegidos] = useState<number[]>([])

  const roles = useQuery({
    queryKey: ["drive", "roles"],
    queryFn: () => api.get<RolResumen[]>("/api/v1/drive/roles"),
  })

  // Sólo se precargan los PROPIOS: precargar los heredados convertiría abrir el
  // panel y guardar sin tocar nada en romper la herencia en silencio.
  useEffect(() => {
    setElegidos(carpeta.permisos_propios ? carpeta.roles : [])
  }, [carpeta])

  const guardar = useMutation({
    mutationFn: () =>
      api.put<Carpeta>(`/api/v1/drive/carpetas/${carpeta.id}/roles`, { roles: elegidos }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["drive", "carpetas"] })
      onCerrar()
    },
  })

  const alternar = (id: number) =>
    setElegidos((previos) =>
      previos.includes(id) ? previos.filter((r) => r !== id) : [...previos, id],
    )

  const heredado = !carpeta.permisos_propios && elegidos.length === 0
  const nombreDe = (id: number) => roles.data?.find((r) => r.id === id)?.nombre ?? `Rol ${id}`

  return (
    <Modal
      abierto
      titulo={`Quién abre «${carpeta.nombre}»`}
      descripcion="Los roles marcados abren esta carpeta y todo lo que cuelgue de ella."
      onCerrar={onCerrar}
    >
      <div className="mb-4 superficie p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
        {heredado ? (
          <>
            Ahora mismo <strong>hereda</strong>:{" "}
            {carpeta.roles.length === 0
              ? "no la abre nadie salvo quien administra el Drive."
              : `la abren ${carpeta.roles.map(nombreDe).join(", ")}.`}{" "}
            Marcando roles aquí dejará de heredar.
          </>
        ) : (
          <>
            Lo que marques <strong>reemplaza</strong> a lo que venga de las carpetas de arriba, no
            se suma. Dejarlo sin marcar nada vuelve a heredar.
          </>
        )}
      </div>

      {roles.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando roles…
        </p>
      )}

      <ul className="mb-4 space-y-1">
        {roles.data?.map((rol) => (
          <li key={rol.id}>
            <label className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-sm toque-cristal">
              <input
                type="checkbox"
                checked={elegidos.includes(rol.id)}
                onChange={() => alternar(rol.id)}
              />
              {rol.nombre}
            </label>
          </li>
        ))}
      </ul>

      {guardar.isError && (
        <p role="alert" className="mb-3 text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error instanceof ErrorApi
            ? guardar.error.message
            : "No se pudo guardar el reparto"}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton variante="primario" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
      </div>
    </Modal>
  )
}
